import React, { useState, useEffect } from "react";
import FileUpload from "../component/FileUpload";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import MediaService from "../../../services/KangarooServices/MediaServices";
import MediaGroupService from "../../../services/KangarooServices/MediaGroupService";
import toast from "react-hot-toast";
import Select from "react-select";

// MediaForm.jsx - main changes only
const MediaForm = ({ isOpen, toggle, fetchMedia, selectedItem }) => {
  const [selectedFiles, setSelectedFiles] = useState([]); // new File objects
  const [existingFiles, setExistingFiles] = useState([]); // existing media
  const [mediaGrpId, setMediaGrpId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mediaGroup, setMediaGroup] = useState([]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setExistingFiles([]);
      setMediaGrpId(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Load existing data when editing
  useEffect(() => {
    if (isOpen && selectedItem) {
      setMediaGrpId(selectedItem.mediaGroupId || null);
      setExistingFiles([
        {
          id: selectedItem.id,
          name: selectedItem.fileName,
          url: selectedItem.url,
          mimeType: selectedItem.mimeType,
          size: selectedItem.size,
          isExisting: true,
        },
      ]);
    } else {
      // Clear existing files for create mode
      setExistingFiles([]);
      setMediaGrpId(null);
    }
  }, [isOpen, selectedItem]);

  const handleFileSelect = (filesArray) => {
    setSelectedFiles((prev) => {
      const newFiles = filesArray.map((f) => f.file);
      // Filter out duplicates by name & size
      const combined = [...prev, ...newFiles];
      const uniqueFiles = combined.filter(
        (file, index, self) =>
          index ===
          self.findIndex((f) => f.name === file.name && f.size === file.size)
      );
      return uniqueFiles;
    });
  };

  const fetchGroup = async (pageNumber = 1, size = 1000) => {
    try {
      setLoading(true);
      const response = await MediaGroupService.getList({
        pageNumber,
        pageSize: size,
      });
      setMediaGroup(response?.items || []);
    } catch (error) {
      console.error("Error fetching media groups:", error);
      toast.error("Failed to fetch media groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchGroup();
  }, [isOpen]);

  const mediaGroupOptions = mediaGroup.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const handleSubmit = async () => {
    if (
      (selectedFiles.length === 0 && existingFiles.length === 0) ||
      !mediaGrpId
    ) {
      toast.error("Please select at least one file and a media group");
      return;
    }

    const formData = new FormData();
    if (selectedItem?.id) {
      // Append all new files
      selectedFiles.forEach((file) => {
        formData.append("file", file); // ✅ use array format
      });
    } else {
      // Append all new files
      selectedFiles.forEach((file) => {
        formData.append("files", file); // ✅ use array format
      });
    }

    formData.append("mediaGroupId", mediaGrpId);

    try {
      setLoading(true);
      if (selectedItem?.id) {
        await MediaService.update(selectedItem.id, formData);
      } else {
        await MediaService.create(formData);
      }

      toast.success("Media uploaded successfully");
      fetchMedia(); // refresh list in parent
      toggle(); // close modal
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const removeNewFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index) => {
    setExistingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {selectedItem?.id ? "Edit Media" : "Upload Media"}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <Label>Media Group *</Label>
          <Select
            name="mediaGroupId"
            options={mediaGroupOptions}
            value={
              mediaGroupOptions.find((opt) => opt.value === mediaGrpId) || null
            }
            onChange={(selected) =>
              setMediaGrpId(selected ? selected.value : null)
            }
            placeholder="Select media group..."
            isClearable
          />
        </div>

        <Label>Media Files *</Label>
        <FileUpload
          onUpload={handleFileSelect}
          selectedFiles={selectedFiles}
          existingFiles={selectedItem?.id ? existingFiles : []} // never show in create
          onRemoveNew={removeNewFile}
          onRemoveExisting={removeExistingFile}
        />

        {loading && <p className="mt-3">Processing...</p>}
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          type="button"
          onClick={toggle}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            (!selectedFiles.length && !existingFiles.length) ||
            !mediaGrpId
          }
        >
          {loading ? "Uploading..." : selectedItem?.id ? "Update" : "Confirm"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MediaForm;
